<script setup lang="ts">
import { computed, onMounted, watch, provide, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useThemeStore } from './stores/theme'
import { useStrapiStore } from './stores/strapi'
import { useAppStateStore } from './stores/appState'
import { useGlobalAuth } from './composables/useGlobalAuth'
import { usePanels } from './composables/usePanels'
import { usePanelHeightManager } from './composables/usePanelHeightManager'
import { getCurrentSEO } from './composables/useSEO'
import type { Component } from 'vue'

import Backdrop from './components/Backdrop.vue'
import SidePanels from './components/Layout/SidePanels.vue'
import NavigationMobile from './components/NavigationMobile.vue'
import Password from './components/Password.vue'
import Loading from './components/Loading.vue'
import Header from './components/Layout/Header.vue'
import HeaderSkeleton from './components/Layout/HeaderSkeleton.vue'
import Footer from './components/Layout/Footer.vue'
import Overlay from './components/Overlay.vue'
import MetaSEO from './components/MetaSEO.vue'
import MetaSocials from './components/MetaSocials.vue'
import FrozenRouterView from './views/placeholders/FrozenRouterView.vue'

const themeStore = useThemeStore()
const strapiStore = useStrapiStore()
const appStateStore = useAppStateStore()
const route = useRoute()

const { 
  showLoading, 
  showPasswordGate, 
  handlePasswordCorrect: originalHandlePasswordCorrect, 
  handleAnimationComplete,
  isLoadingData,
  dataLoadingCompleted,
  animationCompleted,
  isLoadingComplete
} = useGlobalAuth()

// Animation states from appState (coordinated centrally)
const hideLoading = computed(() => appStateStore.animationStates.loadingHidden)
const isHeaderEntering = computed(() => appStateStore.animationStates.headerEntering)
const isFooterEntering = computed(() => appStateStore.animationStates.footerEntering)


const handlePasswordCorrect = () => {
  originalHandlePasswordCorrect()
  triggerInitialPanelAnimation()
}

const { menuOpen, active, previous, open, close, toggleMenu, closeMenu, handleMobilePanel } = usePanels()
const { currentAppHeight } = usePanelHeightManager()


const isRouteTransitioning = ref(false)
const currentSEOData = getCurrentSEO()

provide('isLoadingData', isLoadingData)
provide('dataLoadingCompleted', dataLoadingCompleted)
provide('animationCompleted', animationCompleted)

// Track the route when panels aren't open for frozen view
const frozenRoute = computed(() => {
  if (active.value) {
    return previous.value
  }
  return null
})

const arePanelsOpen = computed(() => !!active)

const headerTheme = computed(() => {
  if (active.value) {
    if (active.value === 'left') {
      return 'xw-teal-medium'
    } else if (active.value === 'right') {
      return 'xw-yellow'
    }
  }
  return themeStore.currentTheme
})

const isLocalDev = computed(() => {
  return (import.meta as any).env.VITE_IS_PRODUCTION !== 'true'
})

const needsTopPadding = computed(() => {
  if (themeStore.currentPageTopSpacer !== null) {
    return themeStore.currentPageTopSpacer
  }
  
  return !(route.path === '/' || appStateStore.RouteUtils.isPanelRoute(route.path))
})

const showSkeletonHeader = computed(() => {
  return !showLoading.value && !hideLoading.value && isLoadingData.value
})



const handleBackdropClick = () => {
  menuOpen.value ? closeMenu() : close()
}

// Trigger panel animation for initial load cases - optimized for GSAP
const triggerInitialPanelAnimation = () => {
  // Only trigger route transitions for non-panel routes
  if (!appStateStore.RouteUtils.isPanelRoute(route.path)) {
    isRouteTransitioning.value = true
    setTimeout(() => {
      isRouteTransitioning.value = false
    }, 700) // Coordinated with content fade-in timing
  }
}

const onRouteEnter = () => {
  // Only apply route transitions for non-panel routes
  if (!appStateStore.RouteUtils.isPanelRoute(route.path)) {
    document.body.classList.add('route-transitioning')
    // GSAP-optimized timing for panel animation
    setTimeout(() => {
      isRouteTransitioning.value = false // Panels animate back to normal position
    }, 350) // Matches GSAP route transition duration
  }
}

const onRouteAfterEnter = () => {
  setTimeout(() => {
    document.body.classList.remove('route-transitioning')
  }, 50)
}

const shouldApplyTransitions = computed(() => {
  return !appStateStore.RouteUtils.isPanelRoute(route.path) && !active.value
})

const onAnimationComplete = () => {
  handleAnimationComplete()
  
  // Use appState coordinated animation sequence
  appStateStore.startEntryAnimationSequence()
  
  // Trigger panel animation (existing timing preserved)
  setTimeout(() => {
    triggerInitialPanelAnimation()
  }, 300)
}

const onFadeComplete = () => {
  appStateStore.setLoadingHidden(true)
}

watch(() => route.path, (newPath, oldPath) => {
  themeStore.setPageTopSpacer(null)

  // Use shared route utilities from appState
  if (appStateStore.RouteUtils.isRegularRouteTransition(newPath, oldPath)) {
    // Regular route to regular route - enable route transitions
    isRouteTransitioning.value = true
  }

  // Only scroll for non-panel routes
  if (appStateStore.RouteUtils.shouldScrollToTop(newPath, oldPath)) {
    setTimeout(() => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      })
    }, 500)
  }
})

onMounted(() => {
  // Trigger panel animation if no loading screen is shown and not on a panel route
  if (!showLoading.value && !showPasswordGate.value) {
    triggerInitialPanelAnimation()
    // Also trigger entrance animations for direct visits using appState
    appStateStore.startEntryAnimationSequence()
  }
})

// Reset animation states when showLoading becomes true again (for subsequent visits)
watch(showLoading, (newValue) => {
  if (newValue) {
    appStateStore.resetAnimationStates()
  }
})
</script>

<template>
  <div 
    class="app-container" 
    :class="[themeStore.currentTheme, { 'local-dev': isLocalDev }]"
    :style="{ minHeight: currentAppHeight }"
  >

    <MetaSEO
      :page-seo="currentSEOData.seo"
      :page-title="currentSEOData.title"
      :site-name="strapiStore.siteInfo?.name"
    />
    

    <MetaSocials
      :page-socials="currentSEOData.socials"
      :page-title="currentSEOData.title"
      :page-description="currentSEOData.description"
      :page-image="currentSEOData.image"
      :site-name="strapiStore.siteInfo?.name"
    />

    <div v-if="isLocalDev" class="local-dev-banner">Local data</div>
    <Loading v-if="!hideLoading" @animation-complete="onAnimationComplete" @fade-complete="onFadeComplete" />

    <template v-else>
      <Password
        v-if="showPasswordGate"
        :is-global-password="true"
        :password-config="strapiStore.siteInfo?.websitePassword"
        description="Password protected."
        @password-correct="handlePasswordCorrect"
      />

      <template v-else>
        <Backdrop 
          :active="!!active || menuOpen"
          @click="handleBackdropClick" 
        />

        <SidePanels
          :active="active"
          :is-route-transitioning="isRouteTransitioning"
          @open="open"
          @close="close"
        />

        <NavigationMobile
          :menuOpen="menuOpen"
          :activePanelSide="active"
          @toggleMenu="toggleMenu"
          @togglePanel="handleMobilePanel"
          @closePanels="close"
        />

        <div class="page-wrapper" :style="{ minHeight: currentAppHeight }">
          <HeaderSkeleton 
            v-if="showSkeletonHeader"
            :theme="headerTheme" 
            :panels-open="arePanelsOpen"
            :active-panel="active" 
          />
          
          <Header 
            v-else
            :theme="headerTheme" 
            :panels-open="arePanelsOpen"
            :active-panel="active"
            :is-entering="isHeaderEntering"
          />
          
          <main class="page-content" :class="{ 'has-top-padding': needsTopPadding }">
            <router-view v-if="shouldApplyTransitions" v-slot="{ Component }">
              <transition 
                name="route-fade" 
                mode="out-in"
                @enter="onRouteEnter"
                @after-enter="onRouteAfterEnter"
              >
                <component :is="Component" />
              </transition>
            </router-view>
            <template v-else>
              <FrozenRouterView v-if="active && frozenRoute" :route="frozenRoute" />
              <router-view v-else />
            </template>
          </main>
          <Footer :is-entering="isFooterEntering" />
        </div>
      </template>
    </template>

    <Overlay />
  </div>
</template>

<style lang="scss" scoped>

@use './assets/scss/variables' as *;
@use './assets/scss/mixins' as m;
@use './assets/scss/themes' as *;

html {
  -moz-text-size-adjust: none;
  -webkit-text-size-adjust: none;
  text-size-adjust: none;
  scroll-behavior: smooth; // ✅ ADD THIS
}

// Keep accessibility override (already exists)
@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    scroll-behavior: auto !important; // ✅ Overrides smooth for accessibility
  }
}

.app-container {
  overflow: hidden;
  position: relative;
  min-height: 100vh;
  width: 100%;
  background-color: var(--theme-bg);
  color: var(--theme-text);
  transition: background-color 0.3s, color 0.3s, min-height 0.3s ease;
 
  
  .local-dev-banner{
    text-align: center;
    font-size: 12px;
    background-color: rgba(255, 0, 0, 0.3);
    width: 100%;
    position: fixed;
    z-index: 1000;
  }
}

.page-wrapper {
  min-height: 100vh;
  display: flex;
  flex-direction: column;

}

.page-content {
  width: 100%;
  transition: margin-top 0.3s ease;
  min-height: 50vh;
}

.has-top-padding {
  margin-top: 100px;
}

.route-fade-enter-active {
  transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  transition-delay: 0.1s;
}

.route-fade-leave-active {
  transition: all 0.3s cubic-bezier(0.55, 0.06, 0.68, 0.19);
}

.route-fade-enter-from {
  opacity: 0;
  transform: translateY(20px);
  filter: blur(2px);
}

.route-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
  filter: blur(1px);
}

.route-fade-enter-to,
.route-fade-leave-from {
  opacity: 1;
  transform: translateY(0);
  filter: blur(0);
}



// Prevent main page scrolling when panels are open - Keep scroll on HTML for sticky positioning
:global(html.panel-open) {
  overflow-y: scroll !important;
  overflow-x: hidden !important;
  height: 100vh !important;
  max-height: 100vh !important;
}

:global(body.panel-open) {
  overflow: hidden !important;
  height: 100vh !important;
  max-height: 100vh !important;
  position: fixed !important;  // Prevents background scroll on mobile
  width: 100% !important;
}

@media (max-width: $breakpoint-lg) {
  footer {
    grid-template-columns: 1fr;
    gap: $spacing-xl;
    text-align: center;
    
    .social-icons {
      justify-content: center;
    }
  }
}

@media (max-width: $breakpoint-sm) {


  .mobile-menu-button,
  .mobile-home-button {
    display: flex;
  }
  
  .mobile-nav-bar {
    position: fixed;
    bottom: $spacing-lg;
    left: 0;
    right: 0;
    display: flex;
    justify-content: space-around;
    align-items: center;
    z-index: 95;
    padding: 0 $spacing-md;
  }

  
}
</style>

